/**
* Static menu definition — the single source of truth for sidebar navigation.
*
* Environment-based filtering:
*   • eu.pons.io  → restricted set (pilot mode — hides unreleased features)
*   • everywhere else (dev/staging/localhost) → full menu
*
* Org-type filtering:
*   Each menu item has an `allowed_org_types` field:
*   • 'all'                  → visible to every org type
*   • comma-separated list   → visible only to those org types
*
*   Org types: law_firm, solo_lawyer, business, individual_client, in_house_team
*
* Role-based filtering uses `allowed_roles`:
*   • 'all'        → visible to everyone
*   • 'admin_only' → visible only to org admins (handled by Sidebar is_org_admin check)
*
* Layout:
*   The sidebar has two visual zones:
*   1. Flat navigation (is_parent: false) — always-visible items, no collapsible headers
*      Items with divider_after: true render a visual separator after them.
*   2. Collapsible sections (is_parent: true) — expandable groups for less-frequent features
*/

import { MenuItem } from '../services/MenuService';
import { isMatterEconomicsEnabled } from '../utils/config';
import { isEU, isDev } from './region';

const ALL = 'all';
const ADMIN_ONLY = 'admin_only';

/** Org types that are NOT individual_client */
const NON_CLIENT = 'law_firm,solo_lawyer,business,in_house_team';

/* ─────────────────────────────────────────────────────────────
   Environment detection
   ───────────────────────────────────────────────────────────── */
/* The region predicates live in `region.ts` so non-menu code (surface
   layouts, hooks, the settings registry) can ask without importing the menu.
   Re-exported here because this is where the app has always read them. */
export { isEU, isDev } from './region';

/**
* Paths to HIDE per environment.
* Add or remove paths here as features become production-ready.
*/
export const EU_HIDDEN_PATHS = new Set([
  '/messages',
  '/analytics',
  // Research is released on EU — '/research' stays OUT of this set (it is the
  // canonical path for both the menu entry and the dashboard tile).
  '/finance',
]);

export const DEV_HIDDEN_PATHS = new Set([
  '/messages',
  '/finance',
]);

/**
 * Returns true if `path` is hidden in the current environment. The sidebar
 * uses these sets to filter menu items; reuse this helper anywhere else
 * (dashboard CTAs, deep-linked buttons) that exposes a feature-area path
 * so EU/Dev hiding stays consistent across the app.
 */
export function isPathHiddenInCurrentEnv(path: string): boolean {
  if (isEU())  return EU_HIDDEN_PATHS.has(path);
  if (isDev()) return DEV_HIDDEN_PATHS.has(path);
  return false;
}

/* ─────────────────────────────────────────────────────────────
   Full menu structure

   Access matrix (org-type → visible pages):
   ┌───────────────────┬──────────┬─────────────┬──────────┬───────────────────┬───────────────┐
   │ Page              │ law_firm │ solo_lawyer  │ business │ individual_client │ in_house_team │
   ├───────────────────┼──────────┼─────────────┼──────────┼───────────────────┼───────────────┤
   │ Home              │    ✓     │      ✓      │    ✓     │         ✓         │       ✓       │
   │ Projects          │    ✓     │      ✓      │    ✓     │         ✓         │       ✓       │
   │ Matters           │    ✓     │      ✓      │    ✓     │         ✓         │       ✓       │
   │ Contacts          │    ✓     │      ✓      │    ✓     │         ✗         │       ✓       │
   │ Tasks             │    ✓     │      ✓      │    ✓     │         ✓         │       ✓       │
   │ Calendar          │    ✓     │      ✓      │    ✓     │         ✓         │       ✓       │
   │ Messages          │    ✓     │      ✓      │    ✓     │         ✓         │       ✓       │
   │ Notifications     │    ✓     │      ✓      │    ✓     │         ✓         │       ✓       │
   │ Files             │    ✓     │      ✓      │    ✓     │         ✓         │       ✓       │
   │ Research          │    ✓     │      ✓      │    ✓     │         ✗         │       ✓       │
   │ Reviews           │    ✓     │      ✓      │    ✓     │         ✗         │       ✓       │
   │ Tables            │    ✓     │      ✓      │    ✓     │         ✗         │       ✓       │
   │ Templates         │    ✓     │      ✓      │    ✓     │         ✗         │       ✓       │
   │ Data Sources      │    ✓     │      ✓      │    ✓     │         ✗         │       ✓       │
   │ Analytics         │    ✓     │      ✓      │    ✓     │         ✗         │       ✓       │
   │ People            │    ✓     │      ✗      │    ✓     │         ✗         │       ✓       │
   │ Departments       │    ✓     │      ✗      │    ✓     │         ✗         │       ✓       │
   │ Teams             │    ✓     │      ✗      │    ✓     │         ✗         │       ✓       │
   │ Finance           │    ✓     │      ✗      │    ✓     │         ✗         │       ✓       │
   │ Settings          │    ✓     │      ✓      │    ✓     │         ✓         │       ✓       │
   │ Help              │    ✓     │      ✓      │    ✓     │         ✓         │       ✓       │
   │ Account           │    ✓     │      ✓      │    ✓     │         ✓         │       ✓       │
   │ Admin Dashboard   │  (retired — now Settings ▸ Administration; see id 30 below)   │
   └───────────────────┴──────────┴─────────────┴──────────┴───────────────────┴───────────────┘
   ───────────────────────────────────────────────────────────── */
const FULL_MENU: MenuItem[] = [
  // ── Flat navigation: Primary entities ──────────────────────
  {
    id: 1,
    label: 'Home',
    path: '/home',
    icon_key: 'PiHouseLight',
    sort_order: 0,
    is_parent: false,
    allowed_roles: ALL,
    allowed_org_types: ALL,
  },
  {
    id: 7,
    label: 'Projects',
    path: '/projects',
    icon_key: 'PiProjectorScreenChartLight',
    sort_order: 1,
    is_parent: false,
    add_button: true,
    allowed_roles: ALL,
    allowed_org_types: ALL,
  },
  {
    id: 8,
    label: 'Matters',
    path: '/matters',
    icon_key: 'PiBriefcaseLight',
    sort_order: 2,
    is_parent: false,
    add_button: true,
    allowed_roles: ALL,
    allowed_org_types: ALL,
  },
  {
    id: 5,
    label: 'Contacts',
    path: '/contacts',
    icon_key: 'PiAddressBookLight',
    sort_order: 3,
    is_parent: false,
    add_button: true,
    allowed_roles: ALL,
    allowed_org_types: NON_CLIENT,
    divider_after: true,
  },

  // ── Flat navigation: Daily workflow ────────────────────────
  {
    id: 2,
    label: 'Tasks',
    path: '/tasks',
    icon_key: 'PiCheckSquareOffsetLight',
    sort_order: 4,
    is_parent: false,
    add_button: true,
    allowed_roles: ALL,
    allowed_org_types: ALL,
  },
  // Matter Economics — personal timesheet (task #2268). Flag-gated.
  ...(isMatterEconomicsEnabled()
    ? [{
        id: 40,
        label: 'My time',
        path: '/my-time',
        icon_key: 'PiClockLight',
        sort_order: 4.5,
        is_parent: false,
        add_button: false,
        allowed_roles: ALL,
        allowed_org_types: NON_CLIENT,
      } as MenuItem]
    : []),
  {
    id: 3,
    label: 'Calendar',
    path: '/calendar',
    icon_key: 'PiCalendarLight',
    sort_order: 5,
    is_parent: false,
    add_button: false,
    allowed_roles: ALL,
    allowed_org_types: ALL,
  },
  {
    id: 4,
    label: 'Messages',
    path: '/messages',
    icon_key: 'PiChatsCircleLight',
    sort_order: 6,
    is_parent: false,
    add_button: false,
    allowed_roles: ALL,
    allowed_org_types: ALL,
  },
  {
    id: 24,
    label: 'Notifications',
    path: '#',
    icon_key: 'PiBellLight',
    sort_order: 7,
    is_parent: false,
    add_button: false,
    allowed_roles: ALL,
    allowed_org_types: ALL,
  },
  {
    id: 9,
    label: 'Files',
    path: '/files',
    icon_key: 'PiFileLight',
    sort_order: 8,
    is_parent: false,
    add_button: false,
    allowed_roles: ALL,
    allowed_org_types: ALL,
    divider_after: true,
  },

  // ── Section: Tools ──────────────────────────────────────
  {
    id: 22,
    label: 'Tools',
    path: null,
    sort_order: 9,
    is_parent: true,
    allowed_roles: ALL,
    allowed_org_types: NON_CLIENT,
    children: [
      { id: 10, label: 'Research',  path: '/research',  icon_key: 'PiListMagnifyingGlassLight',  sort_order: 1, add_button: false, allowed_roles: ALL, allowed_org_types: NON_CLIENT },
      { id: 31, label: 'Reviews',   path: '/reviews',   icon_key: 'PiFileMagnifyingGlassLight',  sort_order: 2, add_button: true,  allowed_roles: ALL, allowed_org_types: NON_CLIENT },
      // AB#1573 — Drafting (drafts + strategies) is now a top-level tool,
      // no longer a tab inside Matters.
      { id: 28, label: 'Drafting',  path: '/drafting',  icon_key: 'PiPencilSimpleLineLight',     sort_order: 3, add_button: true,  allowed_roles: ALL, allowed_org_types: NON_CLIENT },
      { id: 11, label: 'Tables',    path: '/tables',    icon_key: 'PiTableLight',                sort_order: 4, add_button: true,  allowed_roles: ALL, allowed_org_types: NON_CLIENT },
    ],
  },

  // ── Section: Knowledge ──────────────────────────────────
  {
    id: 35,
    label: 'Knowledge',
    path: null,
    sort_order: 10,
    is_parent: true,
    allowed_roles: ALL,
    allowed_org_types: NON_CLIENT,
    children: [
      { id: 15, label: 'Templates',    path: '/templates',    icon_key: 'PiFileTextLight',  sort_order: 1, add_button: true,  allowed_roles: ALL, allowed_org_types: NON_CLIENT },
      { id: 13, label: 'Data Sources', path: '/data-sources', icon_key: 'PiDatabaseLight',  sort_order: 2, add_button: true,  allowed_roles: ALL, allowed_org_types: NON_CLIENT },
      { id: 12, label: 'Analytics',    path: '/analytics',    icon_key: 'PiChartBarLight',  sort_order: 3, add_button: false, allowed_roles: ALL, allowed_org_types: NON_CLIENT },
    ],
  },

  // ── Section: Organization ───────────────────────────────
  {
    id: 23,
    label: 'Organization',
    path: null,
    sort_order: 11,
    is_parent: true,
    allowed_roles: ALL,
    allowed_org_types: 'law_firm,business,in_house_team',
    children: [
      { id: 16, label: 'People',      path: '/people',      icon_key: 'PiUsersLight',      sort_order: 1, add_button: false, allowed_roles: ALL, allowed_org_types: 'law_firm,business,in_house_team' },
      { id: 17, label: 'Departments', path: '/departments', icon_key: 'PiBuildingLight',   sort_order: 2, add_button: true,  allowed_roles: ALL, allowed_org_types: 'law_firm,business,in_house_team' },
      { id: 18, label: 'Teams',       path: '/teams',       icon_key: 'PiUsersThreeLight', sort_order: 3, add_button: true,  allowed_roles: ALL, allowed_org_types: 'law_firm,business,in_house_team' },
      { id: 19, label: 'Finance',     path: '/finance',     icon_key: 'PiCoinsLight',      sort_order: 4, add_button: false, allowed_roles: ALL, allowed_org_types: 'law_firm,business,in_house_team' },
      // Matter Economics — firm dashboard (task #2281). Flag-gated.
      ...(isMatterEconomicsEnabled()
        /* ADMIN ONLY. This is the FIRM-WIDE dashboard: every matter's
           revenue, every person's billed hours. A member's own time and
           economics live on `/my-time`, which stays open to everyone. */
        ? [{ id: 41, label: 'Economics', path: '/economics', icon_key: 'PiChartLineUpLight', sort_order: 5, add_button: false, allowed_roles: ADMIN_ONLY, allowed_org_types: 'law_firm,business,in_house_team' }]
        : []),
    ],
  },

  // ── Bottom bar ──────────────────────────────────────────
  {
    id: 25,
    label: 'Bottom',
    path: null,
    sort_order: 12,
    is_parent: true,
    allowed_roles: ALL,
    allowed_org_types: ALL,
    children: [
      { id: 26, label: 'Settings', path: '/settings', icon_key: 'PiGearLight',     sort_order: 1, add_button: false, allowed_roles: ALL, allowed_org_types: ALL },
      { id: 27, label: 'Help',     path: '/help',     icon_key: 'PiQuestionLight', sort_order: 2, add_button: false, allowed_roles: ALL, allowed_org_types: ALL },
    ],
  },

  // ── User account menu ───────────────────────────────────
  {
    id: 28,
    label: 'User',
    path: null,
    sort_order: 13,
    is_parent: true,
    allowed_roles: ALL,
    allowed_org_types: ALL,
    children: [
      { id: 29, label: 'Account',         path: '/account', icon_key: 'PiUserLight', sort_order: 1, add_button: false, allowed_roles: ALL,        allowed_org_types: ALL },
      // Admin Dashboard is retired from the nav — organization administration now
      // lives in Settings ▸ Administration (`/settings/admin-overview`,
      // `/settings/admin-users`, `/settings/admin-roles`, `/settings/admin-audit`).
      // The `/admin` route and AdminPage are untouched and still reachable directly.
      // To re-enable: uncomment the line below AND the matching fallback NavLink in
      // `components/Sidebar/Sidebar.tsx` (search "Admin Dashboard").
      // { id: 30, label: 'Admin Dashboard', path: '/admin',   icon_key: 'PiGearLight', sort_order: 2, add_button: false, allowed_roles: ADMIN_ONLY, allowed_org_types: 'law_firm,business,in_house_team' },
    ],
  },
];

/* ─────────────────────────────────────────────────────────────
   Environment + org-type filtered menu builder
   ───────────────────────────────────────────────────────────── */

/**
* Check whether a menu item is visible for the given org type.
*
* Rules:
*  • 'all' or undefined → always visible
*  • comma-separated list → visible only if orgType is in the list
*  • If orgType is null/undefined (e.g. during onboarding) → show everything
*/
function isAllowedForOrgType(allowedOrgTypes: string | undefined, orgType: string | null | undefined): boolean {
  if (!orgType) return true; // No org yet (onboarding) → show full menu
  if (!allowedOrgTypes || allowedOrgTypes === 'all') return true;
  const allowed = allowedOrgTypes.split(',').map(t => t.trim().toLowerCase());
  return allowed.includes(orgType.toLowerCase());
}

/**
* Returns the menu filtered for the current environment and org type.
*
*  • eu.pons.io  → hides EU_HIDDEN_PATHS
*  • dev.pons.io → hides DEV_HIDDEN_PATHS
*  • localhost / other → full menu
*
* Org-type filtering removes items not relevant to the user's organization type.
*
* admin_only items are kept in the data — the Sidebar handles the
* is_org_admin check at render time.
*
* Standalone items (is_parent: false, e.g. Home, Projects) are filtered directly
* by environment + org type. Sectioned items filter their children.
*
* @param orgType - Organization type from userInfo (e.g. 'law_firm', 'business')
*/
export function getFilteredMenu(orgType?: string | null): MenuItem[] {
  let hiddenPaths: Set<string> | null = null;

  if (isEU())       hiddenPaths = EU_HIDDEN_PATHS;
  else if (isDev()) hiddenPaths = DEV_HIDDEN_PATHS;

  return FULL_MENU
    .map(item => {
      // Standalone items (no children) — return as-is or null
      if (!item.is_parent) {
        if (hiddenPaths && item.path && hiddenPaths.has(item.path)) return null;
        if (!isAllowedForOrgType(item.allowed_org_types, orgType)) return null;
        return item;
      }

      // Sectioned items — filter children
      return {
        ...item,
        children: (item.children || []).filter(child => {
          if (hiddenPaths && child.path && hiddenPaths.has(child.path)) return false;
          if (!isAllowedForOrgType(child.allowed_org_types, orgType)) return false;
          return true;
        }),
      };
    })
    .filter((item): item is MenuItem => {
      if (!item) return false;
      // Drop sections with no remaining children
      if (item.is_parent && (!item.children || item.children.length === 0)) return false;
      // Drop sections where org type doesn't match the parent
      if (item.is_parent && !isAllowedForOrgType(item.allowed_org_types, orgType)) return false;
      return true;
    });
}
